import React, { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useUser, useClerk } from '@clerk/clerk-react'

const Header: React.FC = () => {
  const { isSignedIn, user } = useUser()
  const { signOut } = useClerk()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleSignOut = async () => {
    await signOut()
    window.location.href = '/landing.html'
  }

  const closeMobileMenu = () => {
    setMobileMenuOpen(false)
  }

  const menuRef = useRef<HTMLUListElement>(null)
  const hamburgerRef = useRef<HTMLButtonElement>(null)

  // Match the landing page's mobile menu behaviour: while the drawer is open,
  // a tap outside it closes it and the page behind is locked from scrolling.
  useEffect(() => {
    if (!mobileMenuOpen) return
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current?.contains(target) || hamburgerRef.current?.contains(target)) return
      setMobileMenuOpen(false)
    }
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('click', onDocClick)
    return () => {
      document.removeEventListener('click', onDocClick)
      document.body.style.overflow = prevOverflow
    }
  }, [mobileMenuOpen])

  // 2026-07-12 UX simplification: the global "Invite Collaborators" and
  // "Collaboration Dashboard" menu items (and their which-project picker)
  // were removed after a live usability walk showed our non-technical target
  // user couldn't connect them to her goal. Inviting family & friends now
  // lives on each piece's card on My Eulogies & Tributes — one plainly
  // named door, in the context of the person it's about.

  return (
    <>
    <header
      style={{
        position: 'sticky',
        top: 0,
        width: '100%',
        padding: '1rem 4rem',
        zIndex: 99999,
        background: 'rgba(234, 232, 228, 0.98)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(25, 40, 92, 0.1)',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
        boxSizing: 'border-box',
      }}
    >
      <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Logo — matches landing page exactly */}
        <a
          href="/landing.html"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.75rem',
            fontWeight: 600,
            color: '#19285c',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <img
            src="/Assets/GhostQuillLogo.png"
            alt="Cyrano"
            style={{ width: 32, height: 32, objectFit: 'contain' }}
          />
          Cyrano
        </a>

        {/* Hamburger — mobile */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem',
            minWidth: 44,
            minHeight: 44,
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
          }}
          ref={hamburgerRef}
          className={`hamburger-btn${mobileMenuOpen ? ' active' : ''}`}
        >
          <span style={{ display: 'block', width: 25, height: 3, background: '#19285c', borderRadius: 2, transition: 'transform 0.3s ease, opacity 0.1s ease' }} />
          <span style={{ display: 'block', width: 25, height: 3, background: '#19285c', borderRadius: 2, transition: 'transform 0.3s ease, opacity 0.1s ease' }} />
          <span style={{ display: 'block', width: 25, height: 3, background: '#19285c', borderRadius: 2, transition: 'transform 0.3s ease, opacity 0.1s ease' }} />
        </button>

        {/* Nav links — matches landing page style */}
        <ul
          ref={menuRef}
          className={`nav-links-react ${mobileMenuOpen ? 'active' : ''}`}
          style={{
            display: 'flex',
            gap: '1.5rem',
            listStyle: 'none',
            alignItems: 'center',
            margin: 0,
            padding: 0,
          }}
        >
          {isSignedIn ? (
            <>
              <li>
                <a
                  href="/landing.html"
                  onClick={closeMobileMenu}
                  style={navLinkStyle}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                >
                  Home
                </a>
              </li>
              <li>
                <Link
                  to="/dashboard"
                  onClick={closeMobileMenu}
                  style={navLinkStyle}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                >
                  My Eulogies &amp; Tributes
                </Link>
              </li>
              <li>
                <Link
                  to="/new-project"
                  onClick={closeMobileMenu}
                  style={navCtaStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#131e40'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#19285c'
                    e.currentTarget.style.transform = 'none'
                  }}
                >
                  + New Eulogy or Tribute
                </Link>
              </li>
              <li>
                <a
                  href="/planning-a-farewell"
                  onClick={closeMobileMenu}
                  style={navLinkStyle}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                >
                  Planning a Farewell
                </a>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <img
                  src="/Assets/200-handdrawn-icons-by-heloholo/png/48/contact48.png"
                  alt=""
                  style={{ width: 24, height: 24, objectFit: 'contain', opacity: 0.7 }}
                />
                <span style={{ ...navLinkStyle, fontWeight: 400 }}>
                  {user?.firstName || 'User'}
                </span>
              </li>
              <li>
                <button
                  onClick={handleSignOut}
                  style={navLinkStyle}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                >
                  Sign Out
                </button>
              </li>
            </>
          ) : (
            <>
              <li>
                <a
                  href="/planning-a-farewell"
                  onClick={closeMobileMenu}
                  style={navLinkStyle}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                >
                  Planning a Farewell
                </a>
              </li>
              <li>
                <Link
                  to="/sign-in"
                  onClick={closeMobileMenu}
                  style={navLinkStyle}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                >
                  Log In
                </Link>
              </li>
              <li>
                <Link
                  to="/sign-up"
                  onClick={closeMobileMenu}
                  style={navCtaStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#131e40'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#19285c'
                    e.currentTarget.style.transform = 'none'
                  }}
                >
                  Get Started
                </Link>
              </li>
            </>
          )}
        </ul>
      </nav>

      {/* Responsive CSS injected inline */}
      <style>{`
        .hamburger-btn {
          display: none !important;
        }
        /* Hamburger animates into an X when open — matches the landing page */
        .hamburger-btn.active span:nth-child(1) {
          transform: translateY(8px) rotate(45deg);
        }
        .hamburger-btn.active span:nth-child(2) {
          opacity: 0;
          transform: scaleX(0);
        }
        .hamburger-btn.active span:nth-child(3) {
          transform: translateY(-8px) rotate(-45deg);
        }
        @media (max-width: 768px) {
          header > nav {
            padding: 0 !important;
          }
          .hamburger-btn {
            display: flex !important;
            position: relative;
            z-index: 50;
          }
          /* Mobile menu: right-side slide-in drawer, matching the landing page */
          .nav-links-react {
            position: fixed !important;
            top: 0;
            right: -100%;
            width: 78%;
            max-width: 320px;
            height: 100vh;
            height: 100dvh;
            background: #ffffff !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            justify-content: flex-start !important;
            margin: 0 !important;
            padding: 92px 2rem 2rem !important;
            gap: 1.25rem !important;
            box-shadow: -2px 0 16px rgba(0, 0, 0, 0.12);
            transition: right 0.3s ease;
            z-index: 40;
            overflow-y: auto;
          }
          .nav-links-react.active {
            right: 0;
          }
          .nav-links-react li {
            width: 100%;
          }
          .nav-links-react a, .nav-links-react button, .nav-links-react span {
            font-size: 1rem !important;
            min-height: 44px;
            display: flex;
            align-items: center;
          }
          .nav-links-react a[style*="background: rgb(25, 40, 92)"],
          .nav-links-react a[style*="background:#19285c"],
          .nav-links-react button[style*="background: rgb(25, 40, 92)"],
          .nav-links-react button[style*="background:#19285c"] {
            width: 100%;
            justify-content: center;
          }
        }
        @media (max-width: 640px) {
          header {
            padding-left: 1.5rem !important;
            padding-right: 1.5rem !important;
          }
        }
      `}</style>
    </header>

    </>
  )
}

// Styles matching the landing page CSS exactly
const navLinkStyle: React.CSSProperties = {
  color: '#060001',
  textDecoration: 'none',
  fontWeight: 400,
  fontSize: '0.95rem',
  transition: 'color 0.2s ease',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontFamily: "'Inter', sans-serif",
}

const navCtaStyle: React.CSSProperties = {
  background: '#19285c',
  color: '#ffffff',
  padding: '0.7rem 1.3rem',
  borderRadius: 50,
  textDecoration: 'none',
  fontWeight: 500,
  fontSize: '0.9rem',
  transition: 'all 0.3s ease',
  border: '2px solid #19285c',
  cursor: 'pointer',
  fontFamily: "'Inter', sans-serif",
  display: 'inline-block',
  minHeight: 44,
  lineHeight: '1.4',
}

const hoverIn = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
  e.currentTarget.style.color = '#19285c'
}

const hoverOut = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
  e.currentTarget.style.color = '#060001'
}

export default Header
